/**
 * Расчёты: типы, запросы, мутации.
 *
 * Все суммы — строки. Это не педантизм: `19770.00` в JS-числе живёт нормально,
 * а сумма за год с копейками уже начинает врать в последнем разряде. Считает
 * сервер, клиент только показывает.
 */

import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query'
import { useRef } from 'react'

import { ApiError, api, newIdempotencyKey } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queries'
import type { MoneyString } from '@/shared/api/types'

export type TimesheetStatus =
  | 'draft'
  | 'sent'
  | 'paid_declared'
  | 'confirmed'
  | 'disputed'

export type AdjustmentType = 'bonus' | 'advance' | 'cash_given' | 'correction' | 'other'

export interface ShiftLine {
  id: number
  date: string
  start?: string | null
  end?: string | null
  minutes_total?: number | null
  minutes_overtime?: number | null
  amount?: MoneyString | null
  edited: boolean
  disputed: boolean
}

export interface ExpenseLine {
  id: number
  title: string
  amount?: MoneyString | null
  occurred_at: string
  receipt_urls: string[]
}

export interface Adjustment {
  id: number
  type: AdjustmentType
  title: string
  amount: MoneyString
  created_at: string
  voided_at?: string | null
  void_reason?: string | null
}

export interface Payee {
  name: string
  phone?: string | null
  bank_hint?: string | null
}

export interface Timesheet {
  id: number
  nanny_id: number
  nanny_name: string
  period_start: string
  period_end: string
  period_title: string
  currency: string
  status: TimesheetStatus
  minutes_total: number
  minutes_overtime: number
  amount_base: MoneyString
  amount_overtime: MoneyString
  amount_expenses: MoneyString
  amount_adjustments: MoneyString
  amount_final: MoneyString
  sent_at?: string | null
  paid_declared_at?: string | null
  confirmed_at?: string | null
  dispute_note?: string | null
  shifts: ShiftLine[]
  expenses: ExpenseLine[]
  adjustments: Adjustment[]
  payee?: Payee | null
  is_current: boolean
}

export interface TimesheetListItem {
  id: number
  period_start: string
  period_end: string
  period_title: string
  status: TimesheetStatus
  amount_final: MoneyString
  currency: string
  minutes_total: number
  confirmed_at?: string | null
}

export interface TimesheetList {
  items: TimesheetListItem[]
  total_minutes: number
  total_paid?: MoneyString | null
}

export const STATUS_LABELS: Record<TimesheetStatus, string> = {
  draft: 'идёт',
  sent: 'ждёт перевода',
  paid_declared: 'перевод отмечен',
  confirmed: 'закрыт',
  disputed: 'есть вопрос',
}

export const ADJUSTMENT_LABELS: Record<AdjustmentType, string> = {
  bonus: 'Премия',
  advance: 'Аванс',
  cash_given: 'Выдано наличными',
  correction: 'Исправление',
  other: 'Другое',
}

/* ─── Запросы ───────────────────────────────────────────────────────────── */

export function useCurrentTimesheet(familyId: number) {
  return useQuery<Timesheet, ApiError>({
    queryKey: queryKeys.timesheet(familyId, 'current'),
    queryFn: () => api.get<Timesheet>('/timesheets/current'),
    staleTime: 15_000,
  })
}

export function useTimesheet(familyId: number, id: number | null) {
  return useQuery<Timesheet, ApiError>({
    queryKey: queryKeys.timesheet(familyId, id ?? 'current'),
    queryFn: () => api.get<Timesheet>(`/timesheets/${id}`),
    enabled: id !== null,
  })
}

export function useTimesheetHistory(familyId: number) {
  return useQuery<TimesheetList, ApiError>({
    queryKey: ['timesheets', familyId, 'history'],
    queryFn: () => api.get<TimesheetList>('/timesheets'),
    staleTime: 60_000,
  })
}

/* ─── Мутации ───────────────────────────────────────────────────────────── */

function useInvalidatePay(familyId: number) {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['timesheet', familyId] })
    void queryClient.invalidateQueries({ queryKey: ['timesheets', familyId] })
  }
}

/** Ключ живёт столько же, сколько экран: повтор не отправит расчёт дважды. */
function useStableKey(): () => string {
  const ref = useRef<string>()
  return () => {
    if (!ref.current) ref.current = newIdempotencyKey()
    return ref.current
  }
}

export function useSendTimesheet(
  familyId: number,
  options?: UseMutationOptions<Timesheet, ApiError, number>,
) {
  const invalidate = useInvalidatePay(familyId)
  const key = useStableKey()
  return useMutation<Timesheet, ApiError, number>({
    mutationFn: (id) =>
      api.post<Timesheet>(`/timesheets/${id}/send`, undefined, { idempotencyKey: key() }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useDeclarePayment(
  familyId: number,
  options?: UseMutationOptions<Timesheet, ApiError, { id: number; note?: string }>,
) {
  const invalidate = useInvalidatePay(familyId)
  const key = useStableKey()
  return useMutation<Timesheet, ApiError, { id: number; note?: string }>({
    mutationFn: ({ id, note }) =>
      api.post<Timesheet>(`/timesheets/${id}/pay`, { note }, { idempotencyKey: key() }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useConfirmPayment(
  familyId: number,
  options?: UseMutationOptions<Timesheet, ApiError, number>,
) {
  const invalidate = useInvalidatePay(familyId)
  const key = useStableKey()
  return useMutation<Timesheet, ApiError, number>({
    mutationFn: (id) =>
      api.post<Timesheet>(`/timesheets/${id}/confirm`, undefined, { idempotencyKey: key() }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useDisputeTimesheet(
  familyId: number,
  options?: UseMutationOptions<Timesheet, ApiError, { id: number; reason: string }>,
) {
  const invalidate = useInvalidatePay(familyId)
  return useMutation<Timesheet, ApiError, { id: number; reason: string }>({
    mutationFn: ({ id, reason }) => api.post<Timesheet>(`/timesheets/${id}/dispute`, { reason }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useResolveDispute(
  familyId: number,
  options?: UseMutationOptions<Timesheet, ApiError, { id: number; resolution: string }>,
) {
  const invalidate = useInvalidatePay(familyId)
  return useMutation<Timesheet, ApiError, { id: number; resolution: string }>({
    mutationFn: ({ id, resolution }) =>
      api.post<Timesheet>(`/timesheets/${id}/resolve`, { resolution }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export interface AdjustmentInput {
  timesheetId: number
  type: AdjustmentType
  title: string
  amount: string
}

export function useAddAdjustment(
  familyId: number,
  options?: UseMutationOptions<Adjustment, ApiError, AdjustmentInput>,
) {
  const invalidate = useInvalidatePay(familyId)
  return useMutation<Adjustment, ApiError, AdjustmentInput>({
    mutationFn: ({ timesheetId, ...body }) =>
      api.post<Adjustment>(`/timesheets/${timesheetId}/adjustments`, body),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useVoidAdjustment(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, { id: number; reason: string }>,
) {
  const invalidate = useInvalidatePay(familyId)
  return useMutation<unknown, ApiError, { id: number; reason: string }>({
    mutationFn: ({ id, reason }) => api.post(`/timesheets/adjustments/${id}/void`, { reason }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useAddAdvance(
  familyId: number,
  options?: UseMutationOptions<Adjustment, ApiError, { amount: string; title?: string }>,
) {
  const invalidate = useInvalidatePay(familyId)
  return useMutation<Adjustment, ApiError, { amount: string; title?: string }>({
    mutationFn: (body) => api.post<Adjustment>('/timesheets/advance', body),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}
